import React, { createRef, useCallback, useEffect, useState } from 'react';
import {
  CheckboxStyles,
  FivePointStyles,
  NumberSliderStyles,
  NpsStyles,
  QuestionContainer,
  RadioStyles,
  TextStyles,
} from './styles';
import { AiOutlineCheck } from 'react-icons/ai';
import { Input, TextArea } from '../Input';
import { FaInfoCircle } from 'react-icons/fa';
import { AnswerBackend } from '../../../views/FormView';

export enum QuestionType {
  multipleChoiceCheckbox = 'multipleChoiceCheckbox',
  multipleChoiceRadio = 'multipleChoiceRadio',
  nps = 'nps',
  fivePointLikertScale = 'fivePointLikertScale',
  openShortText = 'openShortText',
  openLongText = 'openLongText',
  boolean = 'boolean',
  numberSlider = 'numberSlider',
}

export type FeedbackQuestionProps = {
  type: keyof typeof QuestionType;
  title: string;
  hint?: string;
  required?: boolean;
  number?: number;
  answerOptions?: string[];
  errorMessage?: string;
  errored?: boolean;
  locale: string;
  scroll?: boolean;
  hash?: string;
  minimum?: number | null;
  maximum?: number | null;
  allowMore?: boolean | null;
  steper?: number | null;
  updateAnswers?: (answer: AnswerBackend) => void;
};

type QuestionProps = {
  title: string;
  options?: string[];
  limit?: number;
  isEn?: boolean;
  updateAnswers?: (answer: AnswerBackend) => void;
};

export const FeedbackQuestion = ({
  hint,
  required,
  title,
  type,
  number,
  answerOptions,
  errorMessage,
  errored,
  locale,
  scroll,
  hash,
  minimum,
  maximum,
  allowMore,
  steper,
  updateAnswers,
}: FeedbackQuestionProps) => {
  const isEn = locale === 'en';

  const questionRef = createRef<HTMLDivElement>();

  const checkboxLimit = hash === 'departamentos' ? 3 : undefined;

  useEffect(() => {
    if (scroll && questionRef.current)
      questionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [scroll]);

  return (
    <QuestionContainer ref={questionRef} required={!!required} error={!!errored} isEn={isEn}>
      <div className="question">
        {errored && (
          <small className="error-message">
            <FaInfoCircle /> {errorMessage}
          </small>
        )}
        <h2>
          {number !== undefined && <span className="question-circle">{number}</span>}
          {title}
        </h2>
        {hint && <p>{hint}</p>}
      </div>
      {type === 'nps' && <NpsQuestion isEn={isEn} title={title} updateAnswers={updateAnswers} />}
      {type === 'fivePointLikertScale' && (
        <LikertQuestion isEn={isEn} title={title} updateAnswers={updateAnswers} />
      )}
      {type === 'multipleChoiceCheckbox' && (
        <CheckboxQuestion
          title={title}
          options={answerOptions || []}
          limit={checkboxLimit}
          isEn={isEn}
          updateAnswers={updateAnswers}
        />
      )}
      {type === 'multipleChoiceRadio' && (
        <RadioQuestion title={title} options={answerOptions || []} updateAnswers={updateAnswers} />
      )}
      {(type === 'openShortText' || !type) && (
        <ShortTextQuestion title={title} updateAnswers={updateAnswers} />
      )}
      {type === 'openLongText' && <LongTextQuestion title={title} updateAnswers={updateAnswers} />}
      {type === 'boolean' && (
        <BooleanQuestion isEn={isEn} title={title} updateAnswers={updateAnswers} />
      )}
      {type === 'numberSlider' && (
        <NumberSliderQuestion
          title={title}
          minimum={minimum ?? 0}
          maximum={maximum ?? 10}
          allowMore={!!allowMore}
          step={steper ?? 1}
          en={isEn}
          updateAnswers={updateAnswers}
        />
      )}
    </QuestionContainer>
  );
};

const getAutoComplete = (title: string): string | undefined => {
  const t = title.toLowerCase();
  if (t.includes('nome') || t.includes('name')) return 'name';
  if (t.includes('email')) return 'email';
  if (t.includes('telefone') || t.includes('phone') || t.includes('celular')) return 'tel';
  return undefined;
};

const ShortTextQuestion = ({ updateAnswers, title }: QuestionProps) => {
  const inputRef = createRef<HTMLInputElement>();
  return (
    <TextStyles>
      <Input
        ref={inputRef}
        onChange={(e) => updateAnswers?.({ question: title, answer: e.target.value })}
        type="text"
        autoComplete={getAutoComplete(title)}
      />
    </TextStyles>
  );
};

const LongTextQuestion = ({ updateAnswers, title }: QuestionProps) => {
  const inputRef = createRef<HTMLTextAreaElement>();

  return (
    <TextStyles>
      <TextArea
        ref={inputRef}
        onChange={(e) => updateAnswers?.({ question: title, answer: e.target.value })}
      />
    </TextStyles>
  );
};

const RadioQuestion = ({ options, title, updateAnswers }: QuestionProps) => {
  const [selectedOption, setSelectedOption] = useState<string | null>();

  React.useEffect(() => {
    updateAnswers?.({ question: title, answer: selectedOption ? selectedOption.toString() : null });
  }, [selectedOption]);

  return (
    <RadioStyles>
      <ul role="radiogroup">
        {options?.map((option) => (
          <li
            role="radio"
            aria-checked={selectedOption === option}
            key={option}
            className={selectedOption === option ? 'selected' : ''}
          >
            <button onClick={() => setSelectedOption(option === selectedOption ? null : option)}>
              {option}
            </button>
          </li>
        ))}
      </ul>
    </RadioStyles>
  );
};

type CheckboxOptionProps = {
  option: string;
  isSelected: boolean;
  isAtLimit: boolean;
  onToggle: (option: string) => void;
};

const CheckboxOption: React.FC<CheckboxOptionProps> = ({
  option,
  isSelected,
  isAtLimit,
  onToggle,
}) => {
  const isDisabled = isAtLimit && !isSelected;

  return (
    <li
      role="checkbox"
      aria-checked={isSelected}
      aria-disabled={isDisabled}
      className={`${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
    >
      <button disabled={isDisabled} onClick={() => onToggle(option)}>
        {isSelected && <AiOutlineCheck aria-hidden strokeWidth={'30px'} />}
        {option}
      </button>
    </li>
  );
};

const CheckboxQuestion = ({ options, limit, title, isEn, updateAnswers }: QuestionProps) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

  const isAtLimit = limit !== undefined && selectedOptions.length >= limit;

  React.useEffect(() => {
    updateAnswers?.({
      question: title,
      answer: selectedOptions.length === 0 ? null : selectedOptions.join('; '),
    });
  }, [selectedOptions]);

  return (
    <CheckboxStyles>
      <ul role="group">
        {options?.map((option) => (
          <CheckboxOption
            key={option}
            option={option}
            isSelected={selectedOptions.includes(option)}
            isAtLimit={isAtLimit}
            onToggle={(opt) =>
              setSelectedOptions((state) => {
                if (state.includes(opt)) return state.filter((o) => o !== opt);
                if (limit && state.length >= limit) return state;
                return [...state, opt];
              })
            }
          />
        ))}
      </ul>
      {isAtLimit && (
        <p className="limit-message">
          {isEn
            ? `✅Limit of ${limit} options selected`
            : `✅Limite de ${limit} opções selecionadas`}
        </p>
      )}
    </CheckboxStyles>
  );
};

const NpsQuestion = ({ title, updateAnswers, isEn }: QuestionProps) => {
  const [selectedOption, setSelectedOption] = useState<number | null>();

  React.useEffect(() => {
    updateAnswers?.({ question: title, answer: selectedOption ? selectedOption.toString() : null });
  }, [selectedOption]);

  return (
    <NpsStyles>
      <div className="titles">
        <span>{isEn ? 'NOT ALL LIKELY' : 'POUCO PROVÁVEL'}</span>
        <span>{isEn ? 'EXTREME LIKELY' : 'MUITO PROVÁVEL'}</span>
      </div>
      <ul role="radiogroup">
        {Array.from(Array(10).keys()).map((_, index) => {
          return (
            <li
              role="radio"
              aria-checked={selectedOption === index + 1}
              className={selectedOption === index + 1 ? 'selected' : ''}
              key={index}
            >
              <button
                style={{
                  outlineColor: index <= 5 ? '#E95223' : index < 8 ? ' #FDC023' : '#66B44E',
                  backgroundColor: index <= 5 ? '#E95223' : index < 8 ? ' #FDC023' : '#66B44E',
                }}
                onClick={() => setSelectedOption(index + 1 === selectedOption ? null : index + 1)}
              >
                {index + 1}
              </button>
            </li>
          );
        })}
      </ul>
    </NpsStyles>
  );
};

const BooleanQuestion = ({ isEn, title, updateAnswers }: QuestionProps) => {
  const [selectedOption, setSelectedOption] = useState<string | null>();

  React.useEffect(() => {
    updateAnswers?.({ question: title, answer: selectedOption ? selectedOption.toString() : null });
  }, [selectedOption]);

  const options = isEn ? ['Yes', 'No'] : ['Sim', 'Não'];

  return (
    <RadioStyles>
      <ul role="radiogroup">
        {options.map((option) => (
          <li
            role="radio"
            aria-checked={selectedOption === option}
            key={option}
            className={selectedOption === option ? 'selected' : ''}
          >
            <button onClick={() => setSelectedOption(option === selectedOption ? null : option)}>
              {option}
            </button>
          </li>
        ))}
      </ul>
    </RadioStyles>
  );
};

type NumberSliderQuestionProps = {
  title: string;
  minimum: number;
  maximum: number;
  en: boolean;
  allowMore: boolean;
  step: number;
  updateAnswers?: (answer: AnswerBackend) => void;
};

const NumberSliderQuestion = ({
  title,
  minimum,
  maximum,
  allowMore,
  step,
  en,
  updateAnswers,
}: NumberSliderQuestionProps) => {
  const [value, setValue] = useState<number | null>(minimum);
  const [isDragging, setIsDragging] = useState(false);
  const trackRef = React.useRef<HTMLDivElement>(null);
  const maxLabel = allowMore ? (en ? `More than ${maximum}` : `Mais de ${maximum}`) : maximum;
  const moreAnswer = en ? `More than ${maximum}` : `Mais de ${maximum}`;

  const onValueChange = useCallback(
    (newValue: number) => {
      setValue(newValue);
      const answerValue = allowMore && newValue === maximum ? moreAnswer : newValue.toString();
      updateAnswers?.({ question: title, answer: answerValue });
    },
    [allowMore, maximum, en, title, updateAnswers],
  );

  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', () => {});
      document.removeEventListener('mouseup', () => {});
    };
  }, []);

  const getPercentage = () => {
    if (value === null) return 0;
    return ((value - minimum) / (maximum - minimum)) * 100;
  };

  const snapToStep = (value: number) => {
    const snapped = Math.round((value - minimum) / step) * step + minimum;
    return Math.max(minimum, Math.min(maximum, snapped));
  };

  const getValueFromPosition = (clientX: number) => {
    if (!trackRef.current) return minimum;
    const rect = trackRef.current.getBoundingClientRect();
    const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const rawValue = minimum + percentage * (maximum - minimum);
    return snapToStep(rawValue);
  };

  const handleTrackMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    onValueChange(getValueFromPosition(e.clientX));
    setIsDragging(true);

    const handleMouseMove = (moveEvent: MouseEvent) => {
      onValueChange(getValueFromPosition(moveEvent.clientX));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDragging(true);

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const newValue = getValueFromPosition(moveEvent.clientX);
      onValueChange(newValue);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const currentValue = value ?? minimum;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        e.preventDefault();
        onValueChange(Math.min(maximum, currentValue + step));
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        e.preventDefault();
        onValueChange(Math.max(minimum, currentValue - step));
        break;
      case 'Home':
        e.preventDefault();
        onValueChange(minimum);
        break;
      case 'End':
        e.preventDefault();
        onValueChange(maximum);
        break;
    }
  };

  const percentage = getPercentage();

  return (
    <NumberSliderStyles style={{ marginTop: '20px' }}>
      <div className="slider-container">
        <div className="slider-wrapper">
          <span className="slider-label min">{minimum}</span>
          <div ref={trackRef} className="slider-track" onMouseDown={handleTrackMouseDown}>
            <div className="slider-bar" style={{ width: `${percentage}%` }} />
            <div
              className={`slider-thumb ${isDragging ? 'dragging' : ''} ${
                value === null ? 'hidden' : ''
              }`}
              style={{ left: `${percentage}%` }}
              onMouseDown={handleMouseDown}
              onKeyDown={handleKeyDown}
              tabIndex={0}
              role="slider"
              aria-valuemin={minimum}
              aria-valuemax={maximum}
              aria-valuenow={value ?? undefined}
            >
              {value !== null && (
                <span className="thumb-label" style={{ top: '-28px', bottom: 'auto' }}>
                  {value}
                </span>
              )}
            </div>
          </div>
          <span className="slider-label max">{maxLabel}</span>
        </div>
      </div>
    </NumberSliderStyles>
  );
};

const LikertQuestion = ({ title, isEn, updateAnswers }: QuestionProps) => {
  const [selectedOption, setSelectedOption] = useState<LikertValues | null>();

  React.useEffect(() => {
    updateAnswers?.({ question: title, answer: selectedOption || null });
  }, [selectedOption]);

  return (
    <FivePointStyles>
      <ul role="radiogroup">
        {Object.entries(likertReactions).map((likertReaction) => {
          const [value, Reaction] = likertReaction as [
            value: LikertValues,
            Reaction: () => JSX.Element,
          ];
          return (
            <li
              role="radio"
              aria-checked={selectedOption === value}
              key={value}
              className={selectedOption === value ? 'selected' : ''}
            >
              <button onClick={() => setSelectedOption(selectedOption === value ? null : value)}>
                <Reaction aria-hidden />
              </button>
              <label>{isEn ? value : likertLabelsPT[value]}</label>
            </li>
          );
        })}
      </ul>
    </FivePointStyles>
  );
};

export type LikertValues =
  | 'Strongly disagree'
  | 'Disagree'
  | 'Neither agree nor disagree'
  | 'Agree'
  | 'Strongly agree';

const likertLabelsPT = {
  ['Strongly disagree']: 'Discordo fortemente',
  ['Disagree']: 'Discordo',
  ['Neither agree nor disagree']: 'Nem concordo nem discordo',
  ['Agree']: 'Concordo',
  ['Strongly agree']: 'Concordo fortemente',
};

export const likertReactions: { [key: string]: () => JSX.Element } = {
  ['Strongly disagree']: () => (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M24 0C19.2533 0 14.6131 1.40758 10.6663 4.04473C6.71954 6.68188 3.64341 10.4302 1.8269 14.8156C0.0103989 19.201 -0.464881 24.0266 0.461164 28.6822C1.38721 33.3377 3.67299 37.6141 7.02945 40.9706C10.3859 44.327 14.6623 46.6128 19.3178 47.5388C23.9734 48.4649 28.799 47.9896 33.1844 46.1731C37.5698 44.3566 41.3181 41.2805 43.9553 37.3337C46.5924 33.3869 48 28.7468 48 24C47.9926 17.6371 45.4617 11.5369 40.9624 7.03762C36.4631 2.53835 30.3629 0.00741045 24 0V0ZM24 46C19.6488 46 15.3953 44.7097 11.7775 42.2923C8.15958 39.8749 5.33979 36.439 3.67466 32.419C2.00953 28.3991 1.57386 23.9756 2.42273 19.708C3.27161 15.4404 5.36691 11.5204 8.44366 8.44365C11.5204 5.3669 15.4404 3.2716 19.708 2.42272C23.9756 1.57385 28.3991 2.00952 32.419 3.67465C36.439 5.33978 39.875 8.15957 42.2923 11.7775C44.7097 15.3953 46 19.6488 46 24C45.9934 29.8327 43.6734 35.4247 39.5491 39.549C35.4247 43.6734 29.8327 45.9934 24 46V46Z" />
      <path d="M13.447 19.9L19.447 16.9C19.6134 16.817 19.7534 16.6894 19.8512 16.5313C19.9491 16.3732 20.0009 16.1909 20.0009 16.005C20.0009 15.8191 19.9491 15.6368 19.8512 15.4787C19.7534 15.3206 19.6134 15.1929 19.447 15.11L13.447 12.11L12.553 13.9L16.764 16L12.553 18.105L13.447 19.9Z" />
      <path d="M28.553 16.9L34.553 19.9L35.447 18.11L31.236 16L35.447 13.9L34.553 12.11L28.553 15.11C28.3866 15.1929 28.2466 15.3206 28.1487 15.4787C28.0509 15.6368 27.999 15.8191 27.999 16.005C27.999 16.1909 28.0509 16.3732 28.1487 16.5313C28.2466 16.6894 28.3866 16.817 28.553 16.9V16.9Z" />
      <path d="M31.2941 31.293L28.0051 34.585L24.7151 31.293C24.5275 31.1055 24.2732 31.0002 24.0081 31.0002C23.7429 31.0002 23.4886 31.1055 23.3011 31.293L20.0081 34.586L16.7081 31.292C16.5205 31.1045 16.2662 30.9992 16.0011 30.9992C15.7359 30.9992 15.4816 31.1045 15.2941 31.292L11.2891 35.292L12.7031 36.708L16.0031 33.408L19.3031 36.708C19.4906 36.8955 19.7449 37.0008 20.0101 37.0008C20.2752 37.0008 20.5295 36.8955 20.7171 36.708L24.0091 33.416L27.2991 36.708C27.4901 36.8887 27.7431 36.9893 28.0061 36.9893C28.269 36.9893 28.522 36.8887 28.7131 36.708L32.0001 33.415L35.2911 36.707L36.7051 35.293L32.7051 31.293C32.5142 31.1132 32.2618 31.0131 31.9996 31.0131C31.7373 31.0131 31.485 31.1132 31.2941 31.293V31.293Z" />
    </svg>
  ),
  ['Disagree']: () => (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M24 48C19.2533 48 14.6131 46.5924 10.6663 43.9553C6.71954 41.3181 3.64341 37.5698 1.8269 33.1844C0.0103989 28.799 -0.464881 23.9734 0.461164 19.3178C1.38721 14.6623 3.67299 10.3859 7.02945 7.02945C10.3859 3.67299 14.6623 1.38721 19.3178 0.461164C23.9734 -0.464881 28.799 0.0103989 33.1844 1.8269C37.5698 3.64341 41.3181 6.71954 43.9553 10.6663C46.5924 14.6131 48 19.2533 48 24C47.9926 30.3629 45.4617 36.4631 40.9624 40.9624C36.4631 45.4617 30.3629 47.9926 24 48ZM24 2.00001C19.6488 2.00001 15.3953 3.29029 11.7775 5.70768C8.15958 8.12507 5.33979 11.561 3.67466 15.581C2.00953 19.6009 1.57386 24.0244 2.42273 28.292C3.27161 32.5596 5.36691 36.4796 8.44366 39.5564C11.5204 42.6331 15.4404 44.7284 19.708 45.5773C23.9756 46.4262 28.3991 45.9905 32.419 44.3254C36.439 42.6602 39.875 39.8404 42.2923 36.2226C44.7097 32.6047 46 28.3512 46 24C45.9934 18.1673 43.6734 12.5753 39.5491 8.45097C35.4247 4.3266 29.8327 2.00663 24 2.00001V2.00001Z" />
      <path d="M34.0579 36.333C33.3736 34.2035 32.031 32.3461 30.2236 31.0285C28.4162 29.7108 26.2371 29.0008 24.0004 29.0008C21.7636 29.0008 19.5846 29.7108 17.7772 31.0285C15.9697 32.3461 14.6272 34.2035 13.9429 36.333L12.0569 35.667C12.8784 33.1469 14.476 30.9513 16.6211 29.3944C18.7662 27.8375 21.3488 26.999 23.9994 26.999C26.65 26.999 29.2326 27.8375 31.3777 29.3944C33.5228 30.9513 35.1204 33.1469 35.9419 35.667L34.0579 36.333Z" />
      <path d="M32 19C30.6744 18.9984 29.4036 18.4711 28.4662 17.5338C27.5289 16.5964 27.0016 15.3256 27 14H29C29 14.7956 29.3161 15.5587 29.8787 16.1213C30.4413 16.6839 31.2044 17 32 17C32.7956 17 33.5587 16.6839 34.1213 16.1213C34.6839 15.5587 35 14.7956 35 14H37C36.9984 15.3256 36.4711 16.5964 35.5338 17.5338C34.5964 18.4711 33.3256 18.9984 32 19V19Z" />
      <path d="M16 19C14.6744 18.9984 13.4036 18.4711 12.4662 17.5338C11.5289 16.5964 11.0016 15.3256 11 14H13C13 14.7956 13.3161 15.5587 13.8787 16.1213C14.4413 16.6839 15.2044 17 16 17C16.7956 17 17.5587 16.6839 18.1213 16.1213C18.6839 15.5587 19 14.7956 19 14H21C20.9984 15.3256 20.4711 16.5964 19.5338 17.5338C18.5964 18.4711 17.3256 18.9984 16 19V19Z" />
    </svg>
  ),
  ['Neither agree nor disagree']: () => (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M24 0C19.2533 0 14.6131 1.40758 10.6663 4.04473C6.71954 6.68188 3.64341 10.4302 1.8269 14.8156C0.0103989 19.201 -0.464881 24.0266 0.461164 28.6822C1.38721 33.3377 3.67299 37.6141 7.02945 40.9706C10.3859 44.327 14.6623 46.6128 19.3178 47.5388C23.9734 48.4649 28.799 47.9896 33.1844 46.1731C37.5698 44.3566 41.3181 41.2805 43.9553 37.3337C46.5924 33.3869 48 28.7468 48 24C47.9926 17.6371 45.4617 11.5369 40.9624 7.03762C36.4631 2.53835 30.3629 0.00741045 24 0V0ZM24 46C19.6488 46 15.3953 44.7097 11.7775 42.2923C8.15958 39.8749 5.33979 36.439 3.67466 32.419C2.00953 28.3991 1.57386 23.9756 2.42273 19.708C3.27161 15.4404 5.36691 11.5204 8.44366 8.44365C11.5204 5.3669 15.4404 3.2716 19.708 2.42272C23.9756 1.57385 28.3991 2.00952 32.419 3.67465C36.439 5.33978 39.875 8.15957 42.2923 11.7775C44.7097 15.3953 46 19.6488 46 24C45.9934 29.8327 43.6734 35.4247 39.5491 39.549C35.4247 43.6734 29.8327 45.9934 24 46V46Z" />
      <path d="M36 34H12V36H36V34Z" />
      <path d="M20 15H12V17H20V15Z" />
      <path d="M36 15H28V17H36V15Z" />
    </svg>
  ),
  ['Agree']: () => (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M24 0C19.2533 0 14.6131 1.40758 10.6663 4.04473C6.71954 6.68188 3.64341 10.4302 1.8269 14.8156C0.0103989 19.201 -0.464881 24.0266 0.461164 28.6822C1.38721 33.3377 3.67299 37.6141 7.02945 40.9706C10.3859 44.327 14.6623 46.6128 19.3178 47.5388C23.9734 48.4649 28.799 47.9896 33.1844 46.1731C37.5698 44.3566 41.3181 41.2805 43.9553 37.3337C46.5924 33.3869 48 28.7468 48 24C47.9926 17.6371 45.4617 11.5369 40.9624 7.03762C36.4631 2.53835 30.3629 0.00741045 24 0V0ZM24 46C19.6488 46 15.3953 44.7097 11.7775 42.2923C8.15958 39.8749 5.33979 36.439 3.67466 32.419C2.00953 28.3991 1.57386 23.9756 2.42273 19.708C3.27161 15.4404 5.36691 11.5204 8.44366 8.44365C11.5204 5.3669 15.4404 3.2716 19.708 2.42272C23.9756 1.57385 28.3991 2.00952 32.419 3.67465C36.439 5.33978 39.875 8.15957 42.2923 11.7775C44.7097 15.3953 46 19.6488 46 24C45.9934 29.8327 43.6734 35.4247 39.5491 39.549C35.4247 43.6734 29.8327 45.9934 24 46V46Z" />
      <path d="M24 38C21.0837 37.9966 18.2878 36.8365 16.2256 34.7744C14.1635 32.7122 13.0034 29.9163 13 27H11C11 30.4478 12.3696 33.7544 14.8076 36.1924C17.2456 38.6304 20.5522 40 24 40C27.4478 40 30.7544 38.6304 33.1924 36.1924C35.6304 33.7544 37 30.4478 37 27H35C34.9966 29.9163 33.8365 32.7122 31.7744 34.7744C29.7122 36.8365 26.9163 37.9966 24 38Z" />
      <path d="M32 17C31.2044 17 30.4413 16.6839 29.8787 16.1213C29.3161 15.5587 29 14.7956 29 14H27C27 15.3261 27.5268 16.5979 28.4645 17.5355C29.4021 18.4732 30.6739 19 32 19C33.3261 19 34.5979 18.4732 35.5355 17.5355C36.4732 16.5979 37 15.3261 37 14H35C35 14.7956 34.6839 15.5587 34.1213 16.1213C33.5587 16.6839 32.7956 17 32 17V17Z" />
      <path d="M16 19C17.3256 18.9984 18.5964 18.4711 19.5338 17.5338C20.4711 16.5964 20.9984 15.3256 21 14H19C19 14.7956 18.6839 15.5587 18.1213 16.1213C17.5587 16.6839 16.7956 17 16 17C15.2044 17 14.4413 16.6839 13.8787 16.1213C13.3161 15.5587 13 14.7956 13 14H11C11.0016 15.3256 11.5289 16.5964 12.4662 17.5338C13.4036 18.4711 14.6744 18.9984 16 19V19Z" />
    </svg>
  ),
  ['Strongly agree']: () => (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M24 0C19.2533 0 14.6131 1.40758 10.6663 4.04473C6.71954 6.68188 3.64341 10.4302 1.8269 14.8156C0.0103989 19.201 -0.464881 24.0266 0.461164 28.6822C1.38721 33.3377 3.67299 37.6141 7.02945 40.9706C10.3859 44.327 14.6623 46.6128 19.3178 47.5388C23.9734 48.4649 28.799 47.9896 33.1844 46.1731C37.5698 44.3566 41.3181 41.2805 43.9553 37.3337C46.5924 33.3869 48 28.7468 48 24C47.9926 17.6371 45.4617 11.5369 40.9624 7.03762C36.4631 2.53835 30.3629 0.00741045 24 0V0ZM24 46C19.6488 46 15.3953 44.7097 11.7775 42.2923C8.15958 39.8749 5.33979 36.439 3.67466 32.419C2.00953 28.3991 1.57386 23.9756 2.42273 19.708C3.27161 15.4404 5.36691 11.5204 8.44366 8.44365C11.5204 5.3669 15.4404 3.2716 19.708 2.42272C23.9756 1.57385 28.3991 2.00952 32.419 3.67465C36.439 5.33978 39.875 8.15957 42.2923 11.7775C44.7097 15.3953 46 19.6488 46 24C45.9934 29.8327 43.6734 35.4247 39.5491 39.549C35.4247 43.6734 29.8327 45.9934 24 46V46Z" />
      <path d="M13.447 19.9L19.447 16.9C19.6134 16.817 19.7534 16.6894 19.8512 16.5313C19.9491 16.3732 20.0009 16.1909 20.0009 16.005C20.0009 15.8191 19.9491 15.6368 19.8512 15.4787C19.7534 15.3206 19.6134 15.1929 19.447 15.11L13.447 12.11L12.553 13.9L16.764 16L12.553 18.105L13.447 19.9Z" />
      <path d="M28.553 16.9L34.553 19.9L35.447 18.11L31.236 16L35.447 13.9L34.553 12.11L28.553 15.11C28.3866 15.1929 28.2466 15.3206 28.1487 15.4787C28.0509 15.6368 27.999 15.8191 27.999 16.005C27.999 16.1909 28.0509 16.3732 28.1487 16.5313C28.2466 16.6894 28.3866 16.817 28.553 16.9V16.9Z" />
      <path d="M11 27C11 30.4478 12.3696 33.7544 14.8076 36.1924C17.2456 38.6304 20.5522 40 24 40C27.4478 40 30.7544 38.6304 33.1924 36.1924C35.6304 33.7544 37 30.4478 37 27V26H11V27ZM34.955 28C34.7069 30.7341 33.4455 33.2766 31.4184 35.128C29.3914 36.9795 26.7453 38.006 24 38.006C21.2547 38.006 18.6086 36.9795 16.5816 35.128C14.5545 33.2766 13.2931 30.7341 13.045 28H34.955Z" />
    </svg>
  ),
};
